/**
 * INTAKE BUCKETS — what an uploaded file is FOR, not what it is called.
 *
 * A classifier that names a paper one of nineteen ways is unstable exactly
 * where it matters least. Measured on a real bundle (20 Sep 2026): the order,
 * the appeal form, the statement of facts and the review order were named the
 * same way in every run; an affidavit came back as "other", "exhibit" and
 * "condonation petition", and a covering letter as the form it was forwarding.
 * Showing the officer nineteen options per row, with that noise in them and no
 * way to see the file, asked them to referee a question nobody needed settled.
 *
 * What an intake actually needs to know is small: is the thing every case of
 * this kind REQUIRES here, and is anything obviously missing BEFORE the officer
 * spends ten minutes and the price of a run finding out. So files are sorted
 * into a few buckets, a bucket is `required` or not, and all the fine-grained
 * variance falls INSIDE a bucket, where it changes nothing.
 *
 * FLOW-AGNOSTIC ON PURPOSE. A flow declares a descriptor — its buckets and the
 * two lookup tables — and gets the checklist and the three-way control for
 * free. Nothing here knows what an appeal is.
 */

export interface IntakeBucket<Id extends string> {
  id: Id
  /** What the officer reads: "Order under appeal". */
  label: string
  /** One plain line under it, shown when the bucket is empty. */
  hint: string
  /** A case of this kind cannot be drafted without at least one of these. */
  required: boolean
}

export interface IntakeDescriptor<Id extends string, Role extends string> {
  buckets: readonly IntakeBucket<Id>[]
  /** Where a file goes when nothing below claims it. Never `required`. */
  fallback: Id
  /**
   * SUB-TYPE FIRST. The sub-type ("covering_letter", "affidavit") was right in
   * runs where the role was wrong, because it describes the paper and the role
   * describes its part in the case — which is the harder question.
   */
  bySubType: Readonly<Record<string, Id>>
  byRole: Readonly<Partial<Record<Role, Id>>>
  /**
   * Roles that mean "this belongs to ANOTHER proceeding" — an earlier order
   * tendered as an exhibit, a judgment copy. They overrule the sub-type: an
   * exhibit that happens to BE an order must not tick the "order" box.
   */
  overrulingRoles: readonly Role[]
  /**
   * The role hint sent to the run when the OFFICER moves a file to a bucket.
   * Only a hint — the run reads every file itself — but it is how their
   * correction reaches the run at all.
   */
  hintFor: Readonly<Record<Id, Role>>
}

export function bucketOf<Id extends string, Role extends string>(
  descriptor: IntakeDescriptor<Id, Role>,
  role: Role | null | undefined,
  subType: string | null | undefined,
): Id {
  if (role && descriptor.overrulingRoles.includes(role)) return descriptor.fallback
  const bySub = subType ? descriptor.bySubType[subType] : undefined
  if (bySub) return bySub
  const byRole = role ? descriptor.byRole[role] : undefined
  return byRole ?? descriptor.fallback
}

export interface IntakeBucketStatus<Id extends string> {
  bucket: IntakeBucket<Id>
  filenames: string[]
  /** Required and empty, with every file settled. */
  missing: boolean
}

/**
 * The checklist. `settled` is false while any file is still being read: a
 * required bucket is never reported missing on a half-read bundle, because
 * "you forgot the order" flashing up two seconds before the order is named is
 * worse than saying nothing.
 */
export function bucketChecklist<Id extends string>(
  buckets: readonly IntakeBucket<Id>[],
  files: readonly { filename: string; bucket: Id }[],
  settled: boolean,
): IntakeBucketStatus<Id>[] {
  return buckets.map((bucket) => {
    const filenames = files.filter((f) => f.bucket === bucket.id).map((f) => f.filename)
    return { bucket, filenames, missing: settled && bucket.required && filenames.length === 0 }
  })
}
